/**
 * Taking steady noise (a fan, an air conditioner) out of your own microphone
 * before it is sent: RNNoise, the open-source filter Jitsi uses, run in the
 * browser through the meeting kit's audio hook. On a real call's audio
 * (6 Oct 2026) it made the gaps between words 24 dB quieter, left speech as it
 * was, and took about 2% of one processor core. It is tuned for 48 kHz and
 * still took 21 dB off at 44.1 kHz, so it runs at any rate.
 */
import type { AudioMiddleware } from "@cloudflare/realtimekit";

const PREFERENCE = "fw.live.clean";

/** Can this browser run the filter at all? */
export function canClean(): boolean {
  return typeof AudioWorkletNode !== "undefined" && typeof WebAssembly !== "undefined";
}

function onAPhone(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod|Android/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
}

/**
 * On unless this browser was told otherwise. Off on a phone: a phone already
 * cleans its microphone itself, and the battery is worth more there.
 */
export function wantsClean(): boolean {
  try {
    const kept = window.localStorage.getItem(PREFERENCE);
    if (kept === "on" || kept === "off") return kept === "on";
  } catch {
    /* no storage: the default */
  }
  return !onAPhone();
}

export function keepClean(on: boolean): void {
  try {
    window.localStorage.setItem(PREFERENCE, on ? "on" : "off");
  } catch {
    /* no storage: it holds for this visit */
  }
}

interface Parts {
  Node: typeof import("@sapphi-red/web-noise-suppressor").RnnoiseWorkletNode;
  wasm: ArrayBuffer;
  worklet: string;
}

let parts: Promise<Parts> | null = null;

/** The filter's code, fetched once and only when someone turns it on in a live. */
function load(): Promise<Parts> {
  parts ??= (async () => {
    const [lib, worklet, plain, simd] = await Promise.all([
      import("@sapphi-red/web-noise-suppressor"),
      import("@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url"),
      import("@sapphi-red/web-noise-suppressor/rnnoise.wasm?url"),
      import("@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url"),
    ]);
    const wasm = await lib.loadRnnoise({ url: plain.default, simdUrl: simd.default });
    return { Node: lib.RnnoiseWorkletNode, wasm, worklet: worklet.default };
  })();
  // A load that failed may be tried again on the next go.
  parts.catch(() => {
    parts = null;
  });
  return parts;
}

/** The kit's audio contexts the filter runs in. A browser may start one paused until the page is clicked. */
const contexts = new Set<AudioContext>();

/** Unpause the filter's sound after a click, where the browser held it. */
export function wakeCleaner(): void {
  for (const ctx of contexts) {
    if (ctx.state === "closed") contexts.delete(ctx);
    else if (ctx.state === "suspended") void ctx.resume().catch(() => {});
  }
}

/**
 * The kit's audio hook: it hands over an audio context and links whatever
 * comes back into the microphone's path. A filter that will not start must
 * never silence anyone, so a failure hands back a plain pass-through.
 */
export const cleaner: AudioMiddleware = async (ctx) => {
  contexts.add(ctx);
  try {
    const { Node, wasm, worklet } = await load();
    await ctx.audioWorklet.addModule(worklet);
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    return new Node(ctx, { maxChannels: 1, wasmBinary: wasm });
  } catch (err) {
    console.warn("[live] the noise filter did not start, so the microphone goes through as it is:", err);
    // Why the cast: the hook is typed for worklet nodes, but it only connects what it gets, and a gain node passes sound through unchanged.
    return ctx.createGain() as unknown as AudioWorkletNode;
  }
};
